export function createDistanceMarkers (coordinates, totalDistance, isMetricSystem) {
  const DISTANCE_MARKER_STEP_HUGE = isMetricSystem ? 500 : 804.67; // 1 marker every 500meters
  const DISTANCE_MARKER_STEP_HIGH = isMetricSystem ? 1000 : 1609.34; // 1 marker every 1000meters
  const DISTANCE_MARKER_STEP_LOW = isMetricSystem ? 5000 : 8046.72; // 1 marker every 5000meters
  const DISTANCE_MARKER_STEP_THRESHOLD = 10;

  let distanceBetweenMarkers = 0;
  if (totalDistance <= 3) distanceBetweenMarkers = DISTANCE_MARKER_STEP_HUGE;
  else if (totalDistance > DISTANCE_MARKER_STEP_THRESHOLD) distanceBetweenMarkers = DISTANCE_MARKER_STEP_LOW;
  else distanceBetweenMarkers = DISTANCE_MARKER_STEP_HIGH;

  const markerCoordinates = computeDistanceMarkerCoordinates(coordinates, distanceBetweenMarkers, totalDistance, isMetricSystem);

  const markers = [];

  let index = 1;
  for (const coordinate of markerCoordinates) {
    markers.push(
      {
        value: totalDistance > DISTANCE_MARKER_STEP_THRESHOLD
          ? Math.round(index * distanceBetweenMarkers / DISTANCE_MARKER_STEP_HIGH)
          : index * distanceBetweenMarkers / DISTANCE_MARKER_STEP_HIGH,
        coordinate,
      },
    );
    index++;
  }

  return markers;
}

function toRadians (_number) {
  return (_number * Math.PI) / 180;
}

function toDegrees (_number) {
  return _number * 180 / Math.PI;
}

export function computeDistanceFromCoordinates (coordinates1, coordinates2) {
  const R = 6371000;
  const dLat_2 = toRadians(coordinates2.lat - coordinates1.lat) / 2;
  const dLon_2 = toRadians(coordinates2.long - coordinates1.long) / 2;
  const lat1 = toRadians(coordinates1.lat);
  const lat2 = toRadians(coordinates2.lat);

  // Height isn't taken into account
  const height = 0;

  const a = Math.sin(dLat_2) * Math.sin(dLat_2) + Math.sin(dLon_2) * Math.sin(dLon_2) * Math.cos(lat1) * Math.cos(lat2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return Math.sqrt(Math.pow(R * c, 2) + Math.pow(height, 2));
}

export function computeDistance (pos1, pos2) {
  return computeDistanceFromCoordinates({ long: pos1.long, lat: pos1.lat }, { long: pos2.long, lat: pos2.lat });
}

/**
 * Computes where to place distance markers given a list of coordinates and returns them as a list of long/lat coordinates.
 *
 * @param coordinates a list of coordinates (long/lat) which may represent the track of a route or a user activity
 * @param distanceBetweenMarkers the distance between two markers
 * @param totalDistance the total distance corresponding to the track represented by the coordinates
 * @returns the corresponding list of distance markers as a list of Coordinate.
 */
export function computeDistanceMarkerCoordinates (coordinates, distanceBetweenMarkers, totalDistance, isMetricSystem) {
  if (!coordinates || coordinates.length === 0 || totalDistance * (isMetricSystem ? 1000 : 1609.34) < distanceBetweenMarkers) return [];

  const markerCoordinates = [];

  let nextMarkerPosition = distanceBetweenMarkers;
  let currentPosition = 0; // accumulated distance from starting coordinate to current coordinate

  for (let i = 0; i < coordinates.length - 1; i++) {
    const currentCoordinate = coordinates[i];
    const nextCoordinate = coordinates[i + 1];

    const nextPosition = currentPosition + computeDistanceFromCoordinates(currentCoordinate, nextCoordinate);

    while (nextMarkerPosition <= nextPosition) { // If we should add a marker before the next position
      if (nextPosition === nextMarkerPosition) {
        markerCoordinates.push(nextCoordinate);
      } else {
        const distanceToNextMarker = nextMarkerPosition - currentPosition;
        const distanceToNextPosition = nextPosition - currentPosition;
        const ratio = distanceToNextMarker / distanceToNextPosition;
        const intermediateCoordinate = findIntermediateCoordinate(currentCoordinate, nextCoordinate, ratio);
        markerCoordinates.push(intermediateCoordinate);
      }

      nextMarkerPosition += distanceBetweenMarkers; // moving marker position to next 'step' (1000m -> 2000m etc...)
    }

    currentPosition = nextPosition; // updating accumulated distance from starting coordinate to next position value
  }

  return markerCoordinates;
}

// code copied and slightly adapted from http://www.movable-type.co.uk/scripts/latlong.html intermediatePointTo(point, fraction) method
// Finds the intermediate coordinate on the coordinate1 coordinate2. A ratio of 0.5 being the middle of the segment.

export function findIntermediateCoordinate (coordinate1, coordinate2, fraction) {
  const φ1 = toRadians(coordinate1.lat); const λ1 = toRadians(coordinate1.long);
  const φ2 = toRadians(coordinate2.lat); const λ2 = toRadians(coordinate2.long);

  // distance between points
  const Δφ = φ2 - φ1;
  const Δλ = λ2 - λ1;
  const a = Math.sin(Δφ / 2) * Math.sin(Δφ / 2) +
      Math.cos(φ1) * Math.cos(φ2) * Math.sin(Δλ / 2) * Math.sin(Δλ / 2);
  const δ = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

  const A = Math.sin((1 - fraction) * δ) / Math.sin(δ);
  const B = Math.sin(fraction * δ) / Math.sin(δ);

  const x = A * Math.cos(φ1) * Math.cos(λ1) + B * Math.cos(φ2) * Math.cos(λ2);
  const y = A * Math.cos(φ1) * Math.sin(λ1) + B * Math.cos(φ2) * Math.sin(λ2);
  const z = A * Math.sin(φ1) + B * Math.sin(φ2);

  const φ3 = Math.atan2(z, Math.sqrt(x * x + y * y));
  const λ3 = Math.atan2(y, x);

  const lat = toDegrees(φ3);
  const long = toDegrees(λ3);

  return { long, lat };
}
